import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import ProductRow from './ProductRow';
import { showPrices, groupCategories } from '../constants/settings';
import { MAX_CONTENT_WIDTH, SPACING_MICRO, SPACING_TIGHT, SPACING_COMPACT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION, SPACING_SECTION_LARGE, SPACING_HERO, SPACING_PAGE } from '../constants/layout';
import { COLORS } from '../constants/colors';
import {
  CODA_EXTRABOLD_DISPLAY_SIZE,
  CODA_EXTRABOLD_MOBILE_SIZE,
  FONT_LARGE_TITLE,
  FONT_LARGE_TITLE_MOBILE,
  FONT_TITLE,
  FONT_TITLE_MOBILE,
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_TITLE3,
  FONT_TITLE3_MOBILE,
  FONT_HEADLINE,
  FONT_HEADLINE_MOBILE,
  FONT_SUBHEADLINE,
  FONT_BODY,
  FONT_CALLOUT,
  FONT_FOOTNOTE,
  FONT_CAPTION,
  FONT_CAPTION2,
} from '../constants/typography';
import { DISPLAY_STYLE } from '../constants/displayStyle';
import { getFormattedProductPrice } from '../utils/priceFormatter';
import { HomePreferencesContext } from '../contexts/HomePreferencesContext';
import { ProductsContext } from '../contexts/ProductsContext';

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

// CategorySelect stays the same
// function CategorySelect({ categories, selected, onChange }) {
//   return (
//     <select value={selected} onChange={onChange}>
//       <option value="">All Categories</option>
//       {categories.map(cat => (
//         <option key={cat.id} value={cat.id}>
//           {cat.title}
//         </option>
//       ))}
//     </select>
//   );
// }

// New Hero Section Component with Continuous Scrolling
function HeroSection({ products, imageItems, getCoverImage, categories, homePreferences }) {
  // 1) Only keep products that actually have a cover image
  const productsWithImage = products.filter(product => {
    const coverImage = getCoverImage(product);
    return coverImage && coverImage.imageURL && coverImage.imageURL.trim() !== '';
  });

  // 2) Duplicate that filtered list for seamless infinite scroll
  const featuredProducts = [...productsWithImage, ...productsWithImage];

  const carouselRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollTimeout = useRef(null);
  const scrollPos = useRef(0);

  const handleUserScroll = () => {
    setAutoScroll(false);
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollPos.current = carouselRef.current?.scrollLeft || 0;
    scrollTimeout.current = setTimeout(() => setAutoScroll(true), 2000);
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    scrollPos.current = el.scrollLeft;
    let rafId;
    const tick = () => {
      if (autoScroll) {
        scrollPos.current += 0.5;
        const maxScroll = el.scrollWidth / 2;
        if (scrollPos.current >= maxScroll) {
          scrollPos.current -= maxScroll;
        }
        el.scrollLeft = scrollPos.current;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [autoScroll, featuredProducts.length]);

  const navigate = useNavigate();

  // Previously used window.location.href to navigate to product detail pages, which caused a full
  // browser reload and a blank screen flash. Fixed by using React Router's navigate() for
  // client-side navigation, keeping the app mounted and context data intact.
  const handleProductClick = productId => {
    navigate(`/product/${productId}`);
  };

  // Helper: find the first category title for a given product ID
  const findCategoryTitle = productId => {
    const cat = categories.find(
      c => Array.isArray(c.itemIDs) && c.itemIDs.includes(productId)
    );
    return cat ? cat.title : '';
  };

  // Check if there's any hero text to determine overlay
  const hasHeroText = homePreferences?.heroTitle || homePreferences?.heroSubtitle;

  return (
    <div className="hero-section">
      <div className="hero-content">
        {homePreferences?.heroTitle && (
          <h1 className="hero-title">{homePreferences.heroTitle}</h1>
        )}
        {homePreferences?.heroSubtitle && (
          <p className="hero-subtitle hero-subtitle--constrained">
            {homePreferences.heroSubtitle}
          </p>
        )}

        {homePreferences?.showCarousel && (
          <div
            className="hero-carousel-container"
            ref={carouselRef}
            onWheel={handleUserScroll}
            onTouchStart={handleUserScroll}
            onTouchMove={handleUserScroll}
          >
            <div className="hero-carousel">
              {featuredProducts.map((product, index) => {
                const coverImage = getCoverImage(product);
                const categoryTitle = findCategoryTitle(product.id);
                return (
                  <div
                    key={`${product.id}-${index}`}
                    className="hero-product-card"
                    onClick={() => handleProductClick(product.id)}
                  >
                    <div className="hero-product-image">
                      {coverImage ? (
                        <img
                          src={coverImage.imageURL}
                          alt={product.productName}
                          loading="lazy"
                        />
                      ) : (
                        <div className="hero-product-placeholder">
                          <span className="material-icons">image</span>
                        </div>
                      )}
                    </div>
                    <div className="hero-product-info">
                      {categoryTitle && (
                        <div className="hero-product-category">
                          {categoryTitle}
                        </div>
                      )}
                      <h3 className="hero-product-name">{product.productName}</h3>
                      {showPrices && product.price && (
                        <p className="hero-product-price">${product.price}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .hero-section {
          background: ${homePreferences?.heroBackgroundImageURL
            ? hasHeroText
              ? `linear-gradient(rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0.85)), url(${homePreferences.heroBackgroundImageURL})`
              : `url(${homePreferences.heroBackgroundImageURL})`
            : COLORS.lightBackground};
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          color: var(--color-text-primary);
          padding: ${SPACING_PAGE} 0;
          padding-left: max(0px, env(safe-area-inset-left));
          padding-right: max(0px, env(safe-area-inset-right));
          margin-bottom: 0;
          position: relative;
          overflow: hidden;
          width: 100vw;
          margin-left: calc(-50vw + 50%);
          min-height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          color: var(--color-text-primary);
          width: 100%;
        }

        .hero-title {
          font-size: ${CODA_EXTRABOLD_DISPLAY_SIZE};
          font-weight: 800;
          margin: 0 0 ${SPACING_BUTTON} 0;
          font-family: var(--font-family-sans);
          color: var(--color-text-primary);
          text-shadow: none;
        }

        .hero-subtitle {
          font-size: ${FONT_HEADLINE};
          margin: 0 0 ${SPACING_SECTION_LARGE} 0;
          font-weight: 300;
          color: var(--color-text-secondary);
          padding: 0 ${SPACING_CARD};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
        }

        .hero-subtitle--constrained {
          max-width: ${MAX_CONTENT_WIDTH};
          margin-left: auto;
          margin-right: auto;
        }

        .hero-carousel-container {
          width: 100%;
          overflow-x: auto;
          position: relative;
          scrollbar-width: none;
        }

        .hero-carousel-container::-webkit-scrollbar {
          display: none;
        }

        /* Increased gap: 40px */
        .hero-carousel {
          display: flex;
          gap: ${SPACING_SECTION_LARGE};
        }

        /* 1:1 aspect ratio, no rounded corners, thin border */
       .hero-product-card {
  min-width: 280px;
  width: 280px;
  height: 280px;
  border-radius: 0;
  border: 0.5px solid var(--color-border);
  overflow: hidden;
  transition: transform 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}


        .hero-product-card:hover {
          transform: scale(1.02);
        }

        /* Image covers full square */
        .hero-product-image {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
          flex: 1;
        }

        .hero-product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .hero-product-card:hover .hero-product-image img {
          transform: scale(1.05);
        }

        .hero-product-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.6);
        }

        .hero-product-placeholder .material-icons {
          font-size: 40px;
        }

        /* Info area aligned left */
        .hero-product-info {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.6) 0%,
            rgba(0, 0, 0, 0.4) 40%,
            rgba(0, 0, 0, 0.2) 70%,
            rgba(0, 0, 0, 0.05) 85%,
            rgba(0, 0, 0, 0) 100%
          );
          color: white;
          border-radius: 0;
          padding-top: ${SPACING_SECTION_LARGE};
          text-align: left;
        }

        /* Smaller category label */
        .hero-product-category {
          font-size: ${FONT_CAPTION};
          margin: 0 0 ${SPACING_TIGHT} 0;
          font-weight: 800;
          opacity: 0.85;
          font-family: var(--font-family-sans);
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .hero-product-name {
          font-size: ${FONT_HEADLINE};
          margin: 0 0 ${SPACING_TIGHT} 0;
          font-family: inherit;
          font-weight: 700;
          line-height: 1.3;
          /* larger blur (spread) */
          text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.8);
        }

        .hero-product-price {
          font-size: ${FONT_BODY};
          font-weight: 700;
          color: #4ade80;
          margin: 0;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: ${CODA_EXTRABOLD_MOBILE_SIZE};
          }

          .hero-subtitle {
            font-size: ${FONT_HEADLINE_MOBILE};
            margin-bottom: ${SPACING_SECTION};
          }

          .hero-product-card {
            min-width: 240px;
            width: 240px;
            height: 240px;
          }

          .hero-product-info {
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
          }

          .hero-product-category {
            font-size: ${FONT_CAPTION2};
            margin-bottom: ${SPACING_MICRO};
            letter-spacing: 0.05em;
          }

          .hero-product-name {
            font-size: ${FONT_BODY};
          }

          .hero-product-price {
            font-size: ${FONT_CALLOUT};
          }

          .hero-carousel {
            gap: ${SPACING_SECTION};
          }
        }
      `}</style>
    </div>
  );
}

function InstagramSection() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const scriptSrc = 'https://www.instagram.com/embed.js';
    const processEmbeds = () => {
      if (window.instgrm?.Embeds?.process) {
        window.instgrm.Embeds.process();
      }
    };

    let script = document.querySelector(`script[src="${scriptSrc}"]`);
    const handleLoad = () => {
      script?.setAttribute('data-instgrm-loaded', 'true');
      processEmbeds();
    };

    if (script) {
      if (script.getAttribute('data-instgrm-loaded') === 'true') {
        processEmbeds();
      } else {
        script.addEventListener('load', handleLoad);
      }
    } else {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', handleLoad);
      document.body.appendChild(script);
    }

    return () => {
      script?.removeEventListener('load', handleLoad);
    };
  }, []);

  return (
    <section className="instagram-section">
      <div className="instagram-inner">
        <div className="instagram-copy">
          <p className="instagram-eyebrow">Social</p>
          <h2 className="instagram-title">Catch the daily haul on Instagram</h2>
          <p className="instagram-subtitle">
            Follow <span>@dndseafoodoc</span> for fresh arrivals, behind-the-scenes moments,
            and seasonal specials straight from the market.
          </p>
          <a
            className="instagram-button"
            href="https://www.instagram.com/dndseafoodoc/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Follow us on Instagram
          </a>
        </div>
        <div className="instagram-embed-wrapper">
          <blockquote
            className="instagram-media"
            data-instgrm-permalink="https://www.instagram.com/dndseafoodoc/?utm_source=ig_embed&utm_campaign=loading"
            data-instgrm-version="14"
          >
            <a
              href="https://www.instagram.com/dndseafoodoc/?utm_source=ig_embed&utm_campaign=loading"
              target="_blank"
              rel="noopener noreferrer"
            >
              View this profile on Instagram
            </a>
          </blockquote>
        </div>
      </div>

      <style jsx>{`
        .instagram-section {
          background: linear-gradient(135deg, rgba(13, 13, 13, 0.9), rgba(32, 32, 32, 0.95));
          padding: ${SPACING_PAGE} ${SPACING_CARD};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          display: flex;
          justify-content: center;
          border-top: 1px solid var(--color-border);
          border-bottom: 1px solid var(--color-border);
        }

        .instagram-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${SPACING_HERO};
          align-items: center;
          width: 100%;
          max-width: ${MAX_CONTENT_WIDTH};
          color: var(--color-text-primary);
        }

        .instagram-copy {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_CARD};
        }

        .instagram-eyebrow {
          margin: 0;
          font-size: ${FONT_CAPTION};
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--color-text-muted);
        }

        .instagram-title {
          margin: 0;
          font-size: ${FONT_TITLE2};
          font-family: var(--font-family-sans);
          text-transform: uppercase;
        }

        .instagram-subtitle {
          margin: 0;
          color: var(--color-text-secondary);
          line-height: 1.6;
        }

        .instagram-subtitle span {
          color: var(--color-text-primary);
          font-weight: 600;
        }

        .instagram-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: ${SPACING_BUTTON} ${SPACING_GROUP};
          border-radius: 999px;
          background: var(--color-accent);
          color: var(--color-accent-text);
          font-weight: 600;
          text-decoration: none;
          width: fit-content;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        .instagram-button:hover,
        .instagram-button:focus {
          background: var(--color-accent-hover);
          transform: translateY(-1px);
        }

        .instagram-embed-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .instagram-embed-wrapper :global(blockquote.instagram-media) {
          margin: 0 !important;
          background-color: var(--color-surface) !important;
          border-radius: 16px !important;
          border: 1px solid var(--color-border) !important;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35) !important;
          overflow: hidden;
          max-width: 420px !important;
          width: 100% !important;
        }

        .instagram-embed-wrapper :global(blockquote.instagram-media a) {
          color: var(--color-link) !important;
        }

        @media (max-width: 960px) {
          .instagram-inner {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .instagram-copy {
            align-items: center;
          }

          .instagram-button {
            width: auto;
          }
        }

        @media (max-width: 600px) {
          .instagram-section {
            padding: ${SPACING_HERO} ${SPACING_CARD};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .instagram-title {
            font-size: ${FONT_TITLE_MOBILE};
          }
        }
      `}</style>
    </section>
  );
}

function ReviewsSection() {
  const reviews = [
    {
      id: 'cameron-l',
      name: 'Cameron L.',
      location: 'Westminster, CA',
      date: 'Apr 20, 2025',
      rating: 5,
      paragraphs: [
        {
          text: 'This is my favorite place in Westminster for fresh seafood...',
          highlight: "The uni here is the freshest I've ever tried.",
        },
        {
          text: "It's a family-owned business...",
          highlight: 'They make you feel like part of the family.',
        },
        {
          text: '',
          highlight: 'If you love seafood, you have to try this place.',
        },
      ],
      traits: {
        icon: 'favorite',
        label: 'Family-owned • Fresh seafood • Friendly service',
      },
    },
    {
      id: 'tony-t',
      name: 'Tony T.',
      location: 'Westminster, CA',
      date: 'Jun 5, 2024',
      rating: 5,
      paragraphs: [
        {
          text: "If you're looking for high quality seafood...",
          highlight: 'Their prices best even some of the Japanese markets.',
        },
        {
          text: '',
          highlight: 'The salmon, scallops and the toro were so amazingly fresh.',
        },
        {
          text: 'We were first time customers...',
          highlight: 'the guy working the Sunday morning was super friendly.',
        },
      ],
      traits: {
        icon: 'price_check',
        label: 'Great prices • Premium quality • Friendly staff',
      },
    },
  ];

  return (
    <section className="reviews-section" aria-labelledby="reviews-heading">
      <div className="reviews-inner">
        <div className="reviews-header">
          <p className="reviews-eyebrow">
            <span className="reviews-eyebrow-icon" aria-hidden="true">
              <i className="fab fa-yelp" />
            </span>
            <span className="reviews-eyebrow-text">Reviews</span>
          </p>
          <h2 id="reviews-heading" className="reviews-title">Community Favorites</h2>
          <p className="reviews-subtitle">
            Guests rave about the hospitality, the ultra-fresh seafood, and the prices that keep them coming back.
          </p>
          <a
            className="reviews-button"
            href="https://www.yelp.com/biz/d-and-d-seafood-westminster-2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read more reviews on Yelp
          </a>
        </div>

        <div className="reviews-grid">
          {reviews.map(review => (
            <article key={review.id} className="reviews-card">
              <header className="reviews-card-header">
                <div>
                  <h3 className="reviews-name">{review.name}</h3>
                  <p className="reviews-location">{review.location}</p>
                </div>
                <div className="reviews-meta">
                  <div className="reviews-rating" aria-label={`${review.rating} out of 5 stars`}>
                    <span className="sr-only">{`${review.rating} out of 5 stars`}</span>
                    {Array.from({ length: review.rating }).map((_, index) => (
                      <span key={index} aria-hidden="true" className="reviews-star">
                        ★
                      </span>
                    ))}
                  </div>
                  <span className="reviews-date">{review.date}</span>
                </div>
              </header>

              <div className="reviews-body">
                {review.paragraphs.map((paragraph, index) => (
                  <p key={index} className="reviews-paragraph">
                    {paragraph.text && <span>{paragraph.text} </span>}
                    {paragraph.highlight && (
                      <span className="reviews-highlight">{paragraph.highlight}</span>
                    )}
                  </p>
                ))}
              </div>

              <footer className="reviews-footer">
                <span className="material-icons reviews-footer-icon" aria-hidden="true">
                  {review.traits.icon}
                </span>
                <span className="reviews-footer-text">{review.traits.label}</span>
              </footer>
            </article>
          ))}
        </div>
      </div>

      <style jsx>{`
        .reviews-section {
          background: linear-gradient(
            135deg,
            rgba(32, 32, 32, 0.95),
            rgba(13, 13, 13, 0.9)
          );
          border-top: 1px solid var(--color-border);
          border-bottom: 1px solid var(--color-border);
          padding: ${SPACING_PAGE} ${SPACING_CARD};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          display: flex;
          justify-content: center;
        }

        .reviews-inner {
          width: 100%;
          max-width: ${MAX_CONTENT_WIDTH};
          display: flex;
          flex-direction: column;
          gap: ${SPACING_HERO};
          color: var(--color-text-primary);
        }

        .reviews-header {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_BUTTON};
          max-width: 640px;
        }

        @media (min-width: 961px) {
          .reviews-header {
            align-self: flex-end;
            text-align: right;
            align-items: flex-end;
          }

          .reviews-button {
            align-self: flex-end;
          }
        }

        .reviews-eyebrow {
          margin: 0;
          display: inline-flex;
          align-items: center;
          gap: ${SPACING_COMPACT};
          font-size: ${FONT_CAPTION};
          text-transform: uppercase;
          color: var(--color-text-muted);
        }

        .reviews-eyebrow-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: ${FONT_CAPTION};
          line-height: 1;
          letter-spacing: normal;
        }

        .reviews-eyebrow-text {
          letter-spacing: 0.2em;
        }

        .reviews-title {
          margin: 0;
          font-family: var(--font-family-sans);
          font-size: ${FONT_TITLE};
          text-transform: uppercase;
        }

        .reviews-subtitle {
          margin: 0;
          color: var(--color-text-secondary);
          line-height: 1.6;
        }

        .reviews-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: ${SPACING_BUTTON} ${SPACING_GROUP};
          border-radius: 999px;
          background: var(--color-link);
          color: var(--color-accent-text);
          font-weight: 600;
          text-decoration: none;
          width: fit-content;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        .reviews-button:hover,
        .reviews-button:focus {
          background: var(--color-link-hover);
          transform: translateY(-1px);
        }

        .reviews-button:focus {
          outline: 2px solid var(--color-link-hover);
          outline-offset: 2px;
        }

        .reviews-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: ${SPACING_SECTION};
        }

        .reviews-card {
          background: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: 16px;
          padding: ${SPACING_SECTION};
          display: flex;
          flex-direction: column;
          gap: ${SPACING_GROUP};
          box-shadow: none;
        }

        .reviews-card-header {
          display: flex;
          justify-content: space-between;
          gap: ${SPACING_CARD};
          align-items: flex-start;
        }

        .reviews-name {
          margin: 0;
          font-size: ${FONT_TITLE3};
          font-weight: 700;
        }

        .reviews-location {
          margin: ${SPACING_TIGHT} 0 0;
          color: var(--color-text-secondary);
        }

        .reviews-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: ${SPACING_COMPACT};
        }

        .reviews-rating {
          display: flex;
          gap: ${SPACING_TIGHT};
          color: #facc15;
          font-size: ${FONT_BODY};
          line-height: 1;
        }

        .reviews-star {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .reviews-date {
          color: var(--color-text-muted);
          font-size: ${FONT_CALLOUT};
        }

        .reviews-body {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_CARD};
          color: var(--color-text-secondary);
          line-height: 1.7;
        }

        .reviews-paragraph {
          margin: 0;
        }

        .reviews-highlight {
          background-color: #fef08a;
          padding: 0 ${SPACING_MICRO};
          line-height: inherit;
          display: inline;
          box-decoration-break: clone;
          -webkit-box-decoration-break: clone;
          color: #854d0e;
        }

        :root[data-theme='dark'] .reviews-highlight {
          background-color: rgba(250, 204, 21, 0.25);
          color: #facc15;
        }

        .reviews-footer {
          display: flex;
          align-items: center;
          gap: ${SPACING_BUTTON};
          padding-top: ${SPACING_CARD_BODY};
          border-top: 1px solid var(--color-border);
          color: var(--color-text-secondary);
        }

        .reviews-footer-icon {
          font-size: 1.25rem;
          color: var(--color-accent);
        }

        .reviews-footer-text {
          font-size: ${FONT_SUBHEADLINE};
        }

        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        @media (max-width: 960px) {
          .reviews-grid {
            grid-template-columns: 1fr;
          }

          .reviews-card {
            padding: ${SPACING_GROUP};
          }
        }

        @media (max-width: 600px) {
          .reviews-section {
            padding: ${SPACING_PAGE} ${SPACING_CARD};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .reviews-title {
            font-size: ${FONT_LARGE_TITLE_MOBILE};
          }

          .reviews-card {
            padding: ${SPACING_GROUP};
          }

          .reviews-card-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .reviews-meta {
            align-items: flex-start;
          }
        }
      `}</style>
    </section>
  );
}

// Grid item component similar to Browse page
function ProductGridItem({ product, coverImage, onClick }) {
  const [loaded, setLoaded] = useState(false);
  const formattedPrice = getFormattedProductPrice(product);

  return (
    <button className="product-grid-btn" onClick={onClick}>
      <div className="product-image-container">
        {coverImage && coverImage.imageURL ? (
          <img
            src={coverImage.imageURL}
            alt={product.productName}
            onLoad={() => setLoaded(true)}
            className={`product-grid-img ${loaded ? 'loaded' : ''}`}
          />
        ) : (
          <div className="product-placeholder">
            <span className="material-icons">image</span>
          </div>
        )}
      </div>
      <div className="product-grid-name">{product.productName}</div>
      {showPrices && formattedPrice && (
        <div className="product-grid-price">{formattedPrice}</div>
      )}
    </button>
  );
}

export default function CatalogView() {
  const { homePreferences } = useContext(HomePreferencesContext);
  const { products, imageItems, getCoverImage } = useContext(ProductsContext);
  const [categories, setCategories] = useState([]);
  const [searchTerm] = useState('');
  const [selectedCategory] = useState('');
  const [loadedThumbnails, setLoadedThumbnails] = useState(new Set());
  const navigate = useNavigate();

  // Get display style from home preferences
  const displayStyle = homePreferences?.displayStyle || DISPLAY_STYLE.GRID;

  // 1) Set document title on mount
  useEffect(() => {
    document.title = 'Products';
  }, []);

  // 2) Fetch all categories once
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'categories'));
      const fetched = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(cat => !!(cat?.title || cat?.name))
        .sort((a, b) => {
          const titleA = (a.title || a.name || '').toLowerCase();
          const titleB = (b.title || b.name || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });

      // Apply the same sorting logic as NavigationBar based on groupCategories setting
      const sortedCategories = groupCategories
        ? fetched // If grouping is on, use original alphabetical order
        : [...fetched].sort((a, b) => {
            // If grouping is off, sort with Product and Clearance at the bottom
            const titleA = (a.title || a.name || '').trim();
            const titleB = (b.title || b.name || '').trim();

            // Clearance always goes to the very bottom
            if (titleA === 'Clearance') return 1;
            if (titleB === 'Clearance') return -1;

            // Product goes above Clearance but below everything else
            if (titleA === 'Product') return 1;
            if (titleB === 'Product') return -1;

            // Everything else is sorted alphabetically
            return titleA.toLowerCase().localeCompare(titleB.toLowerCase());
          });

      setCategories(sortedCategories);
    })();
  }, []);

  // 3) When a thumbnail finishes loading, mark it as loaded
  const handleThumbnailLoad = productId => {
    setLoadedThumbnails(prev => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const search = searchTerm.trim().toLowerCase();

  const isProductActive = product => {
    const deactivatedDate = parseDate(product.deactivatedDate);
    if (!deactivatedDate) return true;
    const today = new Date();
    return today <= deactivatedDate;
  };

  const activeProducts = products.filter(isProductActive);

  // 7) Filter + sort logic
  let filteredProducts = [];
  if (selectedCategory) {
    const cat = categories.find(c => c.id === selectedCategory);
    if (cat) {
      filteredProducts = activeProducts
        .filter(
          prod =>
            prod.productName.toLowerCase().includes(search) &&
            Array.isArray(cat.itemIDs) &&
            cat.itemIDs.includes(prod.id)
        )
        .sort((a, b) => a.productName.localeCompare(b.productName));
    }
  } else {
    filteredProducts = activeProducts
      .filter(prod => prod.productName.toLowerCase().includes(search))
      .sort((a, b) => a.productName.localeCompare(b.productName));
  }

  // Check if hero section should be shown
  const shouldShowHero = homePreferences?.heroTitle ||
                         homePreferences?.heroSubtitle ||
                         homePreferences?.showCarousel ||
                         homePreferences?.heroBackgroundImageURL;

  return (
    <div>
      {/* ★ Hero Section now uses 1:1 images, left-aligned text, and a thin border */}
      {shouldShowHero && (
        <HeroSection
          products={activeProducts}
          imageItems={imageItems}
          getCoverImage={getCoverImage}
          categories={categories}
          homePreferences={homePreferences}
        />
      )}

      {/* <InstagramSection /> */}

      {/* <ReviewsSection /> */}

      <div className="product-list-container">
        {selectedCategory === '' ? (
          <>
            {categories.map(cat => {
              const productsInThisCategory = filteredProducts.filter(prod =>
                Array.isArray(cat.itemIDs) && cat.itemIDs.includes(prod.id)
              );
              if (productsInThisCategory.length === 0) {
                return null;
              }
              return (
                <div key={cat.id} style={{ marginBottom: SPACING_SECTION }}>
                  <h2
                    style={{
                      margin: 0,
                      padding: 0,
                      marginBottom: displayStyle === DISPLAY_STYLE.GRID ? SPACING_CARD : '0px',
                      fontFamily: 'var(--font-family-sans)',
                      fontWeight: 800,
                      fontSize: FONT_TITLE2,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    <Link to={`/category/${cat.id}`} className="category-link">
                      {cat.title}
                    </Link>
                  </h2>
                  {typeof cat.subtitle === 'string' && cat.subtitle.trim() ? (
                    <p className="category-subtitle">{cat.subtitle}</p>
                  ) : null}
                  {displayStyle === DISPLAY_STYLE.GRID ? (
                    <div className="product-grid">
                      {productsInThisCategory.map(product => (
                        <ProductGridItem
                          key={product.id}
                          product={product}
                          coverImage={getCoverImage(product)}
                          onClick={() => navigate(`/product/${product.id}`)}
                        />
                      ))}
                    </div>
                  ) : (
                    productsInThisCategory.map(product => (
                      <ProductRow
                        key={product.id}
                        product={product}
                        coverImage={getCoverImage(product)}
                        isLoaded={loadedThumbnails.has(product.id)}
                        onImageLoad={handleThumbnailLoad}
                      />
                    ))
                  )}
                </div>
              );
            })}
          </>
        ) : (
          displayStyle === DISPLAY_STYLE.GRID ? (
            <div className="product-grid">
              {filteredProducts.map(product => (
                <ProductGridItem
                  key={product.id}
                  product={product}
                  coverImage={getCoverImage(product)}
                  onClick={() => navigate(`/product/${product.id}`)}
                />
              ))}
            </div>
          ) : (
            filteredProducts.map(product => (
              <ProductRow
                key={product.id}
                product={product}
                coverImage={getCoverImage(product)}
                isLoaded={loadedThumbnails.has(product.id)}
                onImageLoad={handleThumbnailLoad}
              />
            ))
          )
        )}

        <style jsx>{`
          .product-list-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: ${SPACING_HERO} ${SPACING_CARD} ${SPACING_HERO};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .category-link {
            color: inherit;
            text-decoration: none;
          }

          .category-link:hover,
          .category-link:focus {
            text-decoration: underline;
          }

          .category-subtitle {
            margin: 0 0 ${SPACING_BUTTON} 0;
            color: var(--color-text-secondary);
          }

          /* Grid layout styles */
          .product-grid {
            display: grid;
            width: 100%;
            row-gap: ${SPACING_BUTTON};
            column-gap: ${SPACING_BUTTON};
            grid-template-columns: repeat(3, 1fr);
          }

          @media (min-width: 1400px) {
            .product-grid {
              grid-template-columns: repeat(4, 1fr);
              column-gap: ${SPACING_BUTTON};
              row-gap: ${SPACING_BUTTON};
            }
          }

          @media (min-width: 1000px) and (max-width: 1399px) {
            .product-grid {
              grid-template-columns: repeat(3, 1fr);
              column-gap: ${SPACING_BUTTON};
              row-gap: ${SPACING_BUTTON};
            }
          }

          @media (max-width: 999px) {
            .product-grid {
              grid-template-columns: repeat(2, 1fr);
              column-gap: ${SPACING_BUTTON};
              row-gap: ${SPACING_BUTTON};
            }
          }

          @media (max-width: 600px) {
            .product-grid {
              grid-template-columns: repeat(2, 1fr);
              column-gap: ${SPACING_BUTTON};
              row-gap: ${SPACING_BUTTON};
            }
          }

          .product-grid-btn {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0;
            border: none;
            background: none;
            cursor: pointer;
            transition: transform 0.2s ease;
          }

          .product-grid-btn:hover {
            transform: translateY(-2px);
          }

          .product-grid-btn:focus {
            outline: 2px solid var(--color-link);
            outline-offset: 2px;
          }

          .product-image-container {
            position: relative;
            width: 100%;
            box-sizing: border-box;
            overflow: hidden;
            border: 1px solid var(--color-border-strong);
            border-radius: 0;
          }

          .product-image-container::before {
            content: '';
            display: block;
            padding-top: 100%;
          }

          .product-grid-img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: block;
            object-fit: cover;
            opacity: 0;
            transition: opacity 0.5s ease-in;
          }

          .product-grid-img.loaded {
            opacity: 1;
          }

          .product-placeholder {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--color-surface);
            color: var(--color-text-muted);
          }

          .product-placeholder .material-icons {
            font-size: 40px;
          }

          .product-grid-name {
            margin-top: 0px;
            font-size: ${FONT_FOOTNOTE};
            color: var(--color-text-primary);
            text-align: center;
            word-break: break-word;
            font-weight: 700;
            line-height: 1.3;
          }

          .product-grid-price {
            margin-top: ${SPACING_TIGHT};
            font-size: ${FONT_FOOTNOTE};
            color: var(--color-text-secondary);
            font-weight: 600;
            text-align: center;
          }
        `}</style>
      </div>
    </div>
  );
}
